/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, FormEvent } from 'react';
import styled from '@emotion/styled';
import { supabase } from '../lib/supabase';
import MapBackground from './MapBackground';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  position: relative;
`;

const Card = styled.div`
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(12px);
  border-radius: 24px;
  padding: 32px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-sizing: border-box;
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 8px;
  font-size: 36px;
  font-weight: 800;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 32px;
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px;
  margin-bottom: 16px;
  background-color: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  color: white;
  font-size: 16px;
  box-sizing: border-box;
  display: block;
  
  &:focus {
    outline: none;
    border-color: #4ade80;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(120deg, #4ade80, #22d3ee);
  border: none;
  border-radius: 12px;
  color: #0f172a;
  font-weight: bold;
  cursor: pointer;
  margin: 16px 0;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMsg = styled.div`
  background-color: rgba(239, 68, 68, 0.15);
  color: #fca5a5;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
`;

const SuccessMsg = styled.div`
  background-color: rgba(74, 222, 128, 0.15);
  color: #86efac;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
`;

const UserEmail = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  margin-bottom: 20px;
  
  strong {
    color: #4ade80;
  }
`;

const ResetPassword = () => {
  // Use refs for form inputs to prevent rerender issues
  const newPasswordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState<string | null>(null);

  // Check for hash parameters on load
  useEffect(() => {
    // Supabase sends the user's email in the hash fragment
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    
    if (type === 'recovery') {
      const userEmail = hashParams.get('email');
      setEmail(userEmail);
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    const newPassword = newPasswordRef.current?.value || '';
    const confirmPassword = confirmPasswordRef.current?.value || '';
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
        
        // Sign out the user first, then redirect
        setTimeout(async () => {
          try {
            // Sign out the user
            await supabase.auth.signOut();
            
            // Force redirect to root (which should show login)
            window.location.replace('/');
          } catch (err) {
            console.error('Error during sign out:', err);
            // Fallback to simple redirect if signOut fails
            window.location.href = '/';
          }
        }, 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Password reset error:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      <MapBackground interactive={true} />
      <Container>
        <Card>
          <Title>JPNWR Maps</Title>
          <Subtitle>Reset Your Password</Subtitle>
          
          {success ? (
            <SuccessMsg>
              Your password has been successfully reset. You will be redirected to the login page in a few seconds.
            </SuccessMsg>
          ) : (
            <form onSubmit={handleSubmit}>
              {email && (
                <UserEmail>
                  Password reset for: <strong>{email}</strong>
                </UserEmail>
              )}
              
              <Input
                ref={newPasswordRef}
                type="password"
                defaultValue=""
                placeholder="New password"
              />
              
              <Input
                ref={confirmPasswordRef}
                type="password"
                defaultValue=""
                placeholder="Confirm password"
              />
              
              {error && <ErrorMsg>{error}</ErrorMsg>}
              
              <Button type="submit" disabled={loading}>
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </Card>
      </Container>
    </>
  );
};

export default ResetPassword; 